(() => {
  if (window.KenzuFooterBooted) return;

  window.KenzuFooterBooted = true;

  const FOOTER_SELECTOR = "[data-kenzu-footer]";
  const PANEL_SELECTOR = "[data-footer-panel]";
  const LOCALIZATION_SELECTOR = "[data-localization-select]";
  const MOBILE_MEDIA_QUERY = "(max-width: 749px)";

  const initializedFooters = new WeakSet();
  const footerControllers = new WeakMap();

  class KenzuFooter {
    constructor(footer) {
      this.footer = footer;
      this.mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.mobileAccordionsEnabled =
        footer.dataset.mobileAccordions === "true";

      this.panels = Array.from(
        footer.querySelectorAll(PANEL_SELECTOR)
      );

      this.localizationSelects = Array.from(
        footer.querySelectorAll(LOCALIZATION_SELECTOR)
      );

      this.abortController = new AbortController();

      this.handlePanelToggle =
        this.handlePanelToggle.bind(this);

      this.handleMediaChange =
        this.handleMediaChange.bind(this);

      this.handleLocalizationChange =
        this.handleLocalizationChange.bind(this);

      this.initialize();
    }

    initialize() {
      const signal = this.abortController.signal;

      this.panels.forEach((panel) => {
        panel.addEventListener(
          "toggle",
          this.handlePanelToggle,
          { signal }
        );
      });

      this.localizationSelects.forEach((select) => {
        select.addEventListener(
          "change",
          this.handleLocalizationChange,
          { signal }
        );
      });

      if (typeof this.mobileMedia.addEventListener === "function") {
        this.mobileMedia.addEventListener(
          "change",
          this.handleMediaChange,
          { signal }
        );
      } else {
        this.mobileMedia.addListener(
          this.handleMediaChange
        );
      }

      this.updatePanelState();
    }

    isMobile() {
      return this.mobileMedia.matches;
    }

    handlePanelToggle(event) {
      if (!this.mobileAccordionsEnabled) return;
      if (!this.isMobile()) return;

      const activePanel = event.currentTarget;

      if (!activePanel.open) return;

      /*
       * En móvil dejamos solamente un acordeón abierto.
       * Esto reduce la altura total del footer.
       */
      this.panels.forEach((panel) => {
        if (panel !== activePanel) {
          panel.open = false;
        }
      });
    }

    handleMediaChange() {
      this.updatePanelState();
    }

    updatePanelState() {
      /*
       * En tablet y escritorio los details deben permanecer
       * abiertos para que todo el contenido sea visible.
       */
      if (!this.isMobile()) {
        this.panels.forEach((panel) => {
          panel.open = true;
        });

        return;
      }

      if (!this.mobileAccordionsEnabled) {
        this.panels.forEach((panel) => {
          panel.open = true;
        });
      }
    }

    handleLocalizationChange(event) {
      const select = event.currentTarget;
      const form = select.closest("form");

      if (!form) return;

      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }

    destroy() {
      this.abortController.abort();

      if (typeof this.mobileMedia.removeEventListener !== "function") {
        this.mobileMedia.removeListener(
          this.handleMediaChange
        );
      }
    }
  }

  const initializeFooter = (footer) => {
    if (!(footer instanceof HTMLElement)) return;
    if (initializedFooters.has(footer)) return;

    const controller = new KenzuFooter(footer);

    initializedFooters.add(footer);
    footerControllers.set(footer, controller);
  };

  const initializeFooters = (root = document) => {
    if (
      root instanceof HTMLElement &&
      root.matches(FOOTER_SELECTOR)
    ) {
      initializeFooter(root);
    }

    root
      .querySelectorAll?.(FOOTER_SELECTOR)
      .forEach(initializeFooter);
  };

  const destroyFooters = (root) => {
    const footers = [];

    if (
      root instanceof HTMLElement &&
      root.matches(FOOTER_SELECTOR)
    ) {
      footers.push(root);
    }

    root
      .querySelectorAll?.(FOOTER_SELECTOR)
      .forEach((footer) => footers.push(footer));

    footers.forEach((footer) => {
      footerControllers.get(footer)?.destroy();
      footerControllers.delete(footer);
    });
  };

  const boot = () => {
    initializeFooters(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  /*
   * Compatibilidad con el editor visual de Shopify.
   */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeFooters(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroyFooters(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:select",
    (event) => {
      const footer =
        event.target.matches?.(FOOTER_SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              FOOTER_SELECTOR
            );

      if (!footer) return;

      /*
       * En el editor abrimos los paneles para facilitar
       * la edición y previsualización de sus bloques.
       */
      footer
        .querySelectorAll(PANEL_SELECTOR)
        .forEach((panel) => {
          panel.open = true;
        });
    }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const panel = event.target.closest?.(
        PANEL_SELECTOR
      );

      if (panel) {
        panel.open = true;
      }
    }
  );
})();